import * as React from 'react';
import { connect } from 'react-redux';

import { getLoggedInStatus, getLoginErrorStatus } from 'auth/selectors';
import { ROUTES } from 'global/constants';
import { RootState } from 'global/state';
import { random } from 'global/utils';

import { SignInLink } from 'elements';
import { Category, ExploreContent } from 'explore/components';
import { Button, StandardPageLayout } from 'ui/components';
import uiMessages from 'ui/components/displayMessages';
import { WrappedMessage } from 'utils';
import { intl } from 'i18n';

import messages from '../displayMessages';
import { PartnerProps, PartnerSection } from './Partners';
import { TestimonialProps, TestimonialSection } from './Testimonials';

import amgenFoundation from 'assets/images/amgen-foundation.png';
import joinUsHexLeft from 'assets/images/join-hex-left.svg';
import joinUsHexRight from 'assets/images/join-hex-right.svg';
import { AmgenBanner } from './AmgenBanner';

const partners: PartnerProps[] = [
    {
        name: 'Khan Academy',
        img: '/assets/images/partners/khan-academy.svg',
    }, {
        name: 'The Concord Consortium',
        img: '/assets/images/partners/concord-consortium.png',
    }, {
        name: 'Wellcome Genome Campus',
        img: '/assets/images/partners/wellcome-genome-campus.jpg',
    }, {
        name: 'iBiology',
        img: '/assets/images/partners/ibiology.png',
    }, {
        name: 'BioBuilder',
        img: '/assets/images/partners/biobuilder.png',
    }, {
        name: 'The Journal of Stories in Science',
        img: '/assets/images/partners/the-journal-of-stories-in-science.png',
    }, {
        name: 'FairOpportunityX',
        img: '/assets/images/partners/fair-opportunity-x.png',
    },
];

const testimonials: TestimonialProps[] = [
    {
        quote: `Universities hold the potential to contribute to global
            betterment by fostering collaboration, challenging conventions, and
            inspiring creativity and innovation. As a digital space that
            invites common effort and brings together scholars from around the
            world, LabXchange is an evolutionary step not only for online
            learning but for education more broadly.`,
        attribution: `Drew Gilpin Faust, President Emerita, Harvard University`,
        img: '/assets/images/testimonials/drew-faust.jpg',
    }, {
        quote: `Everyone needs science, and science needs everyone. At a time
            of remarkable scientific progress, we’re excited by the potential
            of LabXchange to educate and inspire both students and lifelong
            learners of all ages.`,
        attribution: `Robert Bradway, Chairman and Chief Executive Officer, Amgen`,
        img: '/assets/images/testimonials/robert-bradway.jpg',
    }, {
        quote: `I can differentiate for my students on LabXchange by
            personalizing pathways, giving them choice and empowering
            them to learn science at their own pace. In diverse
            classes, this allows me to support students where they are
            and helps everyone meet the learning goals.`,
        attribution: `Mary Liu, Science Teacher, Weston High School`,
        img: '/assets/images/testimonials/mary-liu.jpg',
    }, {
        quote: `LabXchange addresses a major need in the
            educational community - how to find, share, and curate
            great educational resources which are currently
            scattered. The time has come for this platform.`,
        attribution: `Dr. Ron Vale, Founder, President, and Chairman of the
                     Board, iBiology`,
        img: '/assets/images/testimonials/ron-vale.jpg',
    }, {
        quote: `We are a tech-savvy generation, so it’s great to have
            interactive tools like LabXchange that help expose us to new lab
            techniques. It should be incorporated into all biology classes.`,
        attribution: `Ashanti Malcolm-Bates, High School Student`,
        img: '/assets/images/testimonials/ashanti-malcolm-bates.jpg',
    },
];

const featuredCategory: Category = {
    nameMessage: messages.featuredItemsHeading,
    slug: 'weekly',
    subCategories: [],
};

interface HomeContentProps {
    isUserLoggedIn: boolean;
    testimonialIdx: number;
}

export const HomeContent: React.FunctionComponent<HomeContentProps> = (props) => (
    <>
        <div id='hero' className='section navy-gradient'>
            <div className='logo'>
                <h1><WrappedMessage message={messages.heroHeading}/></h1>
                <p><WrappedMessage message={messages.heroText}/></p>
                <div className='hero-buttons'>
                    <Button label={messages.heroExploreButton}
                            href={ROUTES.Explore.HOME}
                            btnStyle='ghost' />
                    {props.isUserLoggedIn ? null :
                        <Button label={messages.heroSignUpButton}
                                href={ROUTES.General.SIGN_UP}
                                btnStyle='primary'/>}
                </div>
            </div>
            <div className='background'></div>
        </div>
        <div id='partners' className='section light'>
            <PartnerSection partners={partners} partnersUrl={ROUTES.Organizations.COLLABORATORS}/>
        </div>
        <AmgenBanner/>
        <div id='how-it-works' className='section navy'>
            <h2><WrappedMessage message={messages.howItWorksHeading}/></h2>
            <div className='heading-underline'></div>
            <div className='how-it-works-row'>
                <div className='how-it-works-col'>
                    <img src='/assets/images/icons/home-explore.svg' alt=''/>
                    <h3><WrappedMessage message={messages.howItWorksExploreHeading}/></h3>
                    <p><WrappedMessage message={messages.howItWorksExploreText}/></p>
                </div>
                <div className='how-it-works-col'>
                    <img src='/assets/images/icons/home-create.svg' alt=''/>
                    <h3><WrappedMessage message={messages.howItWorksCreateHeading}/></h3>
                    <p><WrappedMessage message={messages.howItWorksCreateText}/></p>
                </div>
                <div className='how-it-works-col'>
                    <img src='/assets/images/icons/home-share.svg' alt=''/>
                    <h3><WrappedMessage message={messages.howItWorksShareHeading}/></h3>
                    <p><WrappedMessage message={messages.howItWorksShareText}/></p>
                </div>
            </div>
        </div>
        <div id='featured-content' className='section light'>
            <ExploreContent category={featuredCategory} hideSidebar={true}/>
        </div>
        <div id='testimonials' className='section navy'>
            <TestimonialSection testimonials={testimonials} firstIdx={props.testimonialIdx} />
        </div>
        <div id='sponsor' className='section light'>
            <h2><WrappedMessage message={messages.sponsorHeading}/></h2>
            <a href='https://www.amgen.com/responsibility/amgen-foundation/'
              target='_blank' rel='noreferrer noopener'>
                <img src={amgenFoundation} alt={intl.formatMessage(messages.sponsorAltTag)}/>
            </a>
            <p><WrappedMessage message={messages.sponsorText1}/></p>
            <p><WrappedMessage message={messages.sponsorText2}/></p>
        </div>
        <div id='join-us' className='section navy-gradient'>
            <div className='background left'>
                <img src={joinUsHexLeft} alt=''/>
            </div>
            <div className='join-text'>
                <h2><WrappedMessage message={messages.joinUsHeading}/></h2>
                <SignInLink to={ROUTES.Explore.HOME}
                            signIn={ROUTES.General.SIGN_UP}
                            className='btn btn-outline-primary section-button'
                            isUserLoggedIn={props.isUserLoggedIn}>
                    <WrappedMessage message={uiMessages.uiSignUp}/>
                </SignInLink>
            </div>
            <div className='background right'>
                <img src={joinUsHexRight} alt=''/>
            </div>
        </div>
   </>
);

interface HomeProps {
    isUserLoggedIn: boolean;
    loginError?: null | string;
}

const PrivateHome: React.FunctionComponent<HomeProps> = (
    {isUserLoggedIn, loginError}) =>
(
    <StandardPageLayout
        backgroundStyle='light-header'
        mainClassName='home-page'
    >
        {loginError ? (
            <div className='alert alert-warning'>
              <WrappedMessage message={messages.apploginError} values={{loginError}}/>
            </div>
        ) : null}
        {/* Randomly choose a testimonial to display first */}
        <HomeContent isUserLoggedIn={isUserLoggedIn}
                     testimonialIdx={Math.floor(random(Date.now()) * testimonials.length)}/>
    </StandardPageLayout>
);

export const Home = connect<HomeProps, RootState>(
    (state: RootState): HomeProps => {
        return {
            isUserLoggedIn: getLoggedInStatus(state),
            loginError: getLoginErrorStatus(state),
        };
    },
)(PrivateHome);

export const About: React.FunctionComponent = (props) => {
    window.location.href = ROUTES.General.ABOUT_SITE;
    return null;
};

export const Faq: React.FunctionComponent = (props) => {
    window.location.href = ROUTES.General.FAQ_SITE;
    return null;
};
